import { Box, Typography } from '@mui/material';
import RadioGroup from 'components/common/RadioGroup';
import type { FC } from 'react';
import FeaturedPlayListOutlinedIcon from '@mui/icons-material/FeaturedPlayListOutlined';
import Input from 'components/common/Input';
import Select from 'components/common/Select';
import { ProjectSelect } from 'features/projects';
import { useFormContext } from 'react-hook-form';
import ChipInput from 'components/common/ChipInput';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { GoogleRuleDetails } from '../types';
import { getReferencesOptions } from '../../api';
import type { RuleTypeMutation } from '../../types';
import BasicFieldSkeleton from '../../components/BasicFieldSkeleton';
import {
  NameRuleChangedMessage,
  LabelFormSection,
  BasicSummary,
  NotificationChannelsAutocomplete,
  NotificationLevelSelect,
  NotificationMessageFields,
} from '../../components';
import { useCurrentNetwork } from 'hooks';
import type { BasicGoogleValues } from './EditGoogleRule';
import useRulePageMode from '../../hooks/useRulePageMode';

const LevelSelect = () => {
  const network = useCurrentNetwork();
  const { data: levels } = useSuspenseQuery({
    ...getReferencesOptions(network),
    select: (data) => data.levels.map(({ name, id }) => ({ value: id, label: name })),
  });

  return (
    <Select
      name="level"
      inputLabel="level"
      required
      selectAll={false}
      options={levels}
      // disable because change of level implies on metrics
      disabled
    />
  );
};

const BasicValues: FC<{ rule: GoogleRuleDetails; mode: RuleTypeMutation }> = ({ rule, mode }) => {
  const { isRead } = useRulePageMode();
  const { getFieldState } = useFormContext<BasicGoogleValues>();
  const isNameDirty = getFieldState('name').isDirty && mode === 'edit';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          Rule settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Typography variant="subtitle1" sx={{ lineHeight: 2.5 }}>
            Status
          </Typography>
          <RadioGroup
            row
            name="ruleStatus"
            options={[
              { value: 'active', label: 'Active', disabled: isRead },
              { value: 'paused', label: 'Paused', disabled: isRead },
              { value: 'archived', label: 'Archived', disabled: isRead },
            ]}
          />
        </Box>
      </Box>
      <Box
        sx={(t) => ({
          display: 'flex',
          gap: 2,
          width: '100%',
          flexDirection: 'column',
          border: `solid ${`rgba(0, 8, 46, 0.12)`}`,
          borderRadius: t.borderRadius.md,
          p: 2,
        })}
      >
        <Box>
          <LabelFormSection label="Basic" icon={<FeaturedPlayListOutlinedIcon />} />
        </Box>
        <Box sx={{ display: 'flex', width: '100%', gap: 4 }}>
          <Box sx={{ display: 'flex', width: 'calc(60% - 50px)', gap: 3, flexDirection: 'column' }}>
            <Input name="name" label="name" innerLabel disabled={isRead} />
            {isNameDirty && <NameRuleChangedMessage />}
            <BasicFieldSkeleton>
              <ProjectSelect name="project" inputLabel="project" required disabled={isRead} />
            </BasicFieldSkeleton>
            <BasicFieldSkeleton>
              <LevelSelect />
            </BasicFieldSkeleton>
            <ChipInput name="adAccountIds" inputLabel="ad account ids" disabled={isRead} />
            <Select
              name="type"
              inputLabel="type"
              required
              selectAll={false}
              disabled={isRead}
              options={[
                { value: 'notify', label: 'notify', disabled: isRead },
                { value: 'action', label: 'action', disabled: isRead },
              ]}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Typography variant="subtitle2">Notifications</Typography>
              <BasicFieldSkeleton>
                <NotificationChannelsAutocomplete name="notificationChannels" inputLabel="name" />
              </BasicFieldSkeleton>
              <BasicFieldSkeleton>
                <NotificationLevelSelect />
              </BasicFieldSkeleton>
              <NotificationMessageFields ruleId={rule.id} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flex: 1, gap: 3, flexDirection: 'column' }}>
            <BasicSummary actionTypes={[{ name: rule.data.type, id: rule.data.type }]} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1">Description</Typography>
              <Box sx={{ py: 1.5, borderRadius: 1.5 }}>
                <Input id="description" name="description" multiline disabled={isRead} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BasicValues;
